import * as THREE from 'three';
import { Insole, INSOLE_LENGTH_MM } from 'models/Insole';
import { useDesignStore } from 'context/DesignContext';
import { areNormalsInverted, extrudeAlongY, GetInsoleBox, LoadStl } from '../../Utils/ViewerUtils';
import { STLExporter } from 'three/addons/exporters/STLExporter.js';
import ApiClient from "ApiClient";
import JSZip from 'jszip';
import { useSelectedDesignStore } from 'context/SelectedDesignContext';
import { toast } from 'react-hot-toast';
import { useViewerStore } from 'context/ViewerContext';
import { GCodeRenderer } from '../GCodeViewer/gcode';
import { LineColorizer } from '../GCodeViewer/SegmentColorizer';
import { Color } from "three";
import { ADDITION, Brush, Evaluator, SUBTRACTION } from 'three-bvh-csg';
import { PRINTER_NAMES } from 'models/PrinterSetting';
import { restorePrintHelpers } from 'services/Printerservice';
import { PrinterHelpertSetting } from 'models/PrinterHelperSetting';
import { MeshConfig, meshConfigs } from 'types/stl';

type SandalPrintMeshes = {
    insole: THREE.Mesh;
    main: THREE.Mesh;
    domeSmall: THREE.Mesh;
    domeBig: THREE.Mesh;
    outSole: THREE.Mesh;
};

const api = new ApiClient();

export class GCodeGenerator {

    private statusTickingInterval: NodeJS.Timeout | null = null;
    private insoles: Insole[];
    hardnesses: any[];
    printers: { [side: string]: { id: string | undefined, settingName: string | undefined } } = {};
    printHelpers: (PrinterHelpertSetting | null)[] = [];

    private alpha = [35 * Math.PI / 180, 35 * Math.PI / 180];
    private transformationMatrix: THREE.Matrix4[] = [];
    private topCoverThickness = [1.6, 1.6];
    private isSandal = useDesignStore.getState().insoleDesign?.isSandal;
    private RAMP_LENGTH = 40;

    private partial = useDesignStore.getState().insoleDesign?.autoProperties?.thirdQuarter;
    private meshconfig: MeshConfig;

    private readonly GCODE_COLORS = {
        TRANSPARENT: new Color(),
        INNER: new Color("rgb(255, 223, 247)"),
        OUTER: new Color("rgb(8, 100, 200)"),
        SKIRT: new Color("#ffff00"),
        FILL: new Color("rgb(126, 185, 247)"),
        BOTTOM_FILL: new Color("#ffff00"),
        INTERNAL_FILL: new Color("#00ffff"),
        TOP_FILL: new Color("#ff00ff"),
        DEFAULT: new Color("#0000ff"),
    };

    constructor(insoles: Insole[] | undefined, hardnessValues: any[], printersVal: { [side: string]: { id: string | undefined, settingName: string | undefined } }) {
        this.meshconfig = meshConfigs[useDesignStore.getState().insoleDesign?.category!];

        this.printers = printersVal;
        if (this.printers['right'].settingName == PRINTER_NAMES.IDEAFORMER) {
            this.alpha[0] = 45 * Math.PI / 180;
            this.topCoverThickness[0] = 1.8;
        }
        if (this.printers['left'].settingName == PRINTER_NAMES.IDEAFORMER) {
            this.alpha[1] = 45 * Math.PI / 180;
            this.topCoverThickness[1] = 1.8;
        }
        const matrixR = new THREE.Matrix4().set(
            1, 0, 0, 0,
            0, 1, -1.0 / Math.tan(this.alpha[0]), 0,
            0, 0, 1.0 / Math.sin(this.alpha[0]), 0,
            0, 0, 0, 1
        ).invert();
        const matrixL = new THREE.Matrix4().set(
            1, 0, 0, 0,
            0, 1, -1.0 / Math.tan(this.alpha[1]), 0,
            0, 0, 1.0 / Math.sin(this.alpha[1]), 0,
            0, 0, 0, 1
        ).invert();
        this.transformationMatrix.push(matrixR);
        this.transformationMatrix.push(matrixL);

        this.hardnesses = hardnessValues;
        this.insoles = insoles!;

        if (this.isSandal) this.topCoverThickness = [2.5, 2.5];
    }

    getStatusPercent() {
        return useViewerStore.getState().gcodeStatus.percent;
    }

    setStatus(percent: number, result: string) {
        useViewerStore.getState().setGcodeStatus(percent, result);
    }

    setStatusTicking() {
        if (this.statusTickingInterval) return;
        this.statusTickingInterval = setInterval(() => {
            if (this.getStatusPercent() < 99) {
                this.setStatus(this.getStatusPercent() + 1, useViewerStore.getState().gcodeStatus.result);
            }
        }, 1000);
    }

    setStatusTickingStop() {
        if (this.statusTickingInterval) {
            clearInterval(this.statusTickingInterval);
            this.statusTickingInterval = null;
        }
    }

    createRampGeo(x: number, y0: number, y1: number, z: number) {
        const rampShape = new THREE.Shape();
        rampShape.moveTo(0, 0);
        rampShape.lineTo(z, 0); // z axis
        rampShape.lineTo(z, y1); // y axis
        rampShape.lineTo(0, y0); // y axis
        rampShape.lineTo(0, 0);

        const extrudeSettings = {
            depth: x, //Width of the ramp // x axis
            bavelEnable: false // No rounded edges
        };

        const rampGeometry = new THREE.ExtrudeGeometry(rampShape, extrudeSettings);
        // rampGeometry.rotateX(-Math.PI/2);
        rampGeometry.rotateY(-Math.PI / 2);
        rampGeometry.computeBoundingBox();
        // console.log(rampGeometry.boundingBox?.min.y);
        rampGeometry.translate(x / 2, rampGeometry.boundingBox!.min.y * -1, 0);

        return rampGeometry;
    }

    addBoxOnFrontOfInsole(engraveGeometry: THREE.BufferGeometry, foot: number, x0: number): THREE.BufferGeometry | null {
        try {

            // const insoleGeo = this.prepareGeometry(engraveGeometry)

            // const boxGeometry = new THREE.BoxGeometry(10, 1.5, 60);
            // const cutterGeometry = new THREE.BoxGeometry(1000, 1000, 1000);
            // const material = new THREE.MeshStandardMaterial({ color: 0xff0000 });

            // const boxBrush = new Brush(boxGeometry);
            const engraveBrush = new Brush(engraveGeometry.clone());
            // const cutterBrush = new Brush(cutterGeometry);

            // boxBrush.geometry.computeBoundingBox();

            const evaluator = new Evaluator();
            evaluator.attributes = ['position', 'normal'];
            // cutterBrush.rotation.x = 45 * Math.PI / 180;
            // cutterBrush.position.set(0, 500, 200 + boxBrush.geometry.boundingBox?.max.z);
            // cutterBrush.updateMatrix();
            // cutterBrush.updateMatrixWorld();
            // const boxCut = evaluator.evaluate(boxBrush, cutterBrush, SUBTRACTION);
            const boxCut = new Brush(this.createRampGeo(10, 2, 4, this.RAMP_LENGTH));

            engraveBrush.geometry.computeBoundingBox();
            const meshCenter = new THREE.Vector3();
            engraveBrush.geometry.boundingBox?.getCenter(meshCenter);
            let xOffset = (foot === 0) ? 5 : -5;
            let rampLoc = !this.isSandal ?
                engraveBrush.geometry.boundingBox?.max.z! - 2 :
                engraveBrush.geometry.boundingBox?.min.z! - this.RAMP_LENGTH + 2;
            // boxCut.position.set(meshCenter.x + xOffset, 0, engraveBrush.geometry.boundingBox?.max.z + 5);
            // boxCut.position.set(meshCenter.x + xOffset, 0, engraveBrush.geometry.boundingBox?.max.z - 2);
            boxCut.position.set(x0, 0, rampLoc);
            boxCut.geometry.computeBoundingBox();
            // console.log('boxCut', boxCut.geometry.boundingBox);
            // cutterBrush.rotateZ(45 * Math.PI / 180);
            // cutterBrush.translateZ(engraveBrush.geometry.boundingBox?.max.z + 5 + 500);

            // const boxBrush=this.meshToBrush(boxMesh);
            engraveBrush.updateMatrix();
            engraveBrush.updateMatrixWorld();

            boxCut.updateMatrix();
            boxCut.updateMatrixWorld();

            const mergedMesh = evaluator.evaluate(engraveBrush, boxCut, ADDITION);

            return mergedMesh.geometry;
            // return this.createRampGeo(10, 1.5, 4, 40);
        }
        catch (error) {
            console.log('Error', error);
            // console.log('Error stack', error.stack);
            return null;
        }
    }

    cutMeshByAngle(userAngleRad: number, meshGeo: THREE.BufferGeometry) {
        try {
            const angle = userAngleRad * 180.0 / Math.PI;
            meshGeo.computeBoundingBox();
            const meshBrush = new Brush(meshGeo);

            const cutterShape = new THREE.Shape();
            cutterShape.moveTo(0, 0);
            cutterShape.lineTo(1000, 0); // z axis
            cutterShape.lineTo(1000, 1000); // y axis
            cutterShape.lineTo(0, 1000); // y axis
            cutterShape.lineTo(0, 0);

            const extrudeSettings = {
                depth: 1000, //Width of the ramp // x axis
                bavelEnable: false // No rounded edges
            };

            const cutterGeometry = new THREE.ExtrudeGeometry(cutterShape, extrudeSettings);
            // rampGeometry.rotateX(-Math.PI/2);
            cutterGeometry.rotateY(-Math.PI / 2);
            cutterGeometry.computeBoundingBox();
            const cutterMin = cutterGeometry.boundingBox!.min;
            cutterGeometry.rotateX(userAngleRad);
            cutterGeometry.translate(500, Math.sin(userAngleRad) * 500, meshGeo.boundingBox!.max.z + cutterMin?.z - Math.cos(userAngleRad) * 500);

            const cutterBrush = new Brush(cutterGeometry);

            const evaluator = new Evaluator();
            evaluator.attributes = ['position', 'normal'];

            const result = evaluator.evaluate(meshBrush, cutterBrush, SUBTRACTION);

            return result.geometry.clone();
        }
        catch {
            return null;
        }
    }

    async GenerateGCode(): Promise<THREE.Mesh[]> {
        if (!this.insoles || this.insoles.length === 0) {
            toast.error("No insole design found. Please complete the design first.", { duration: 10000 });
            return [];
        }
        this.setStatus(1, 'engrave');
        this.setStatusTicking();
        const gcodeObjects: THREE.Mesh[] = [];

        this.printHelpers = restorePrintHelpers();
        if (this.printHelpers[0] == null) {
            this.printHelpers = [];
            this.printHelpers.push(new PrinterHelpertSetting());
            this.printHelpers.push(new PrinterHelpertSetting());
            this.printHelpers[1]?.setSide('left');
        }
        const sides = ['right', 'left'] as const;
        const promises = this.insoles.map(async (insole: Insole, insoleIndex: number) => {
            const side = sides[insoleIndex];

            const printerId = this.printers[side].id;
            if (!printerId) return; // Skip if printer not provided

            const printHelper = this.printHelpers.find(temp => temp?.side.toLocaleLowerCase() === side);
            const mainMesh = this.partial && !this.isSandal ? (insole.mainMesh.children[0] as THREE.Mesh).clone() : insole.mainMesh.clone();
            const topCover = this.getTopCover(mainMesh, insoleIndex, this.topCoverThickness[insoleIndex]);

            if (!topCover) {
                toast.error(`Failed to generate Top Cover for the ${side} insole.`, { duration: 10000 });
                return;
            }

            // Yield to event loop to allow status ticking
            await new Promise(resolve => setTimeout(resolve, 0));

            // Engraving insole
            const engraving = useDesignStore.getState().engraving;
            let engravedGeo = engraving.Engrave(mainMesh.geometry.clone(), insoleIndex);

            if (!this.isSandal && !this.partial && printHelper?.hasRamp) {
                const insoleVertices = insole.lowpolyBufferGeometry?.getAttribute('position');
                const x0 = insoleVertices?.getX(this.meshconfig.forefootIndex);
                const res = this.addBoxOnFrontOfInsole(engravedGeo, insoleIndex, x0!);
                if (res !== null) {
                    engravedGeo = res;
                }
            }

            if (!this.isSandal && !this.partial && (printHelper?.cutFront || printHelper?.hasRamp)) {
                const res = this.cutMeshByAngle(this.alpha[insoleIndex], engravedGeo);
                if (res != null) {
                    engravedGeo = res;
                }
            }

            let sandalPrint: SandalPrintMeshes | null = null;
            if (this.isSandal) {
                sandalPrint = await this.buildSandalPrintMeshes(mainMesh, topCover, insoleIndex);
                if (!sandalPrint) {
                    toast.error(`Failed to prepare sandal meshes for the ${side} foot.`, { duration: 10000 });
                    return;
                }

                engravedGeo.rotateY(Math.PI);
                topCover.geometry.rotateY(Math.PI);
                sandalPrint.insole.geometry.rotateY(Math.PI);
                sandalPrint.domeSmall.geometry.rotateY(Math.PI);
                sandalPrint.domeBig.geometry.rotateY(Math.PI);
                sandalPrint.outSole.geometry.rotateY(Math.PI);
            }
            topCover.geometry.rotateX(Math.PI / 2);

            const engravedMesh = new THREE.Mesh(engravedGeo);
            if (sandalPrint) sandalPrint.main = engravedMesh;
            const uploadInsole = sandalPrint?.insole ?? engravedMesh;

            if (insoleIndex + 1 == this.insoles.length) {
                this.setStatus(Math.max(this.getStatusPercent(), 30), 'fetch')
            } else {
                this.setStatus(Math.max(this.getStatusPercent(), 15), 'engrave')
            }

            try {
                const gcode = await this.getGCodeFromServer(insoleIndex, topCover, uploadInsole, printerId, this.hardnesses[insoleIndex], sandalPrint);
                this.setStatus(Math.max(this.getStatusPercent(), 70), 'fetch');
                if (!gcode) return;

                insole.gcodeText = gcode;
                gcodeObjects.push(await this.exportGCode(gcode, uploadInsole, topCover, insoleIndex));

            } catch (error: any) {
                const msg = error?.message || "";

                if (msg.includes("Unauthorized")) {
                    toast.error("Your session has expired. Please log in again.", { duration: 10000 });
                } else if (msg.includes("Bad Request") || msg.includes("400")) {
                    toast.error("GCode generation error. Please check your input and try again.", { duration: 10000 });
                } else {
                    toast.error(`Failed to generate GCode for the ${side} insole.`, { duration: 10000 });
                }

                console.error("GCode generation error:", error);
            }
        });
        await Promise.all(promises);
        this.setStatusTickingStop();
        this.setStatus(100, 'idle');
        setTimeout(() => {
            this.setStatus(0, 'idle');
        }, 2000);
        return gcodeObjects;
    }

    // Utility function to compute top cover geometry from insole
    getTopCover(insole: THREE.Mesh, foot: number, thickness: number) {
        let indices: number[] = [];
        let roofpoints: THREE.Vector3[] = [];
        let selectedIds: number[] = [];

        const topLayer = new THREE.Mesh(insole.geometry.clone());
        const selected = insole.geometry.getAttribute('selected').array;
        const points = topLayer.geometry.attributes.position.array;
        const triangles = topLayer.geometry.getIndex()?.array;

        selected.forEach((value: number, id: number) => {
            if (value > 0.99) {
                roofpoints.push(new THREE.Vector3(
                    points[id * 3],
                    points[id * 3 + 1],
                    points[id * 3 + 2]
                ));

                selectedIds.push(id);
            }
        });

        const normalsInverted = areNormalsInverted(topLayer.geometry);

        if (triangles) {
            for (let i = 0; i < triangles.length; i += 3) {
                if (selectedIds.includes(triangles[i]) && selectedIds.includes(triangles[i + 1]) && selectedIds.includes(triangles[i + 2])) {
                    const a = selectedIds.findIndex(id => id === triangles[i]);
                    const b = selectedIds.findIndex(id => id === triangles[i + 1]);
                    const c = selectedIds.findIndex(id => id === triangles[i + 2]);

                    if (normalsInverted)
                        indices.push(a, b, c);
                    else
                        indices.push(c, b, a);
                }
            }
        }

        topLayer.geometry.setFromPoints(roofpoints);
        topLayer.geometry.setIndex(new THREE.BufferAttribute(new Uint16Array(indices), 1));
        topLayer.geometry.computeVertexNormals();

        const topCover = extrudeAlongY(topLayer, thickness, 'relative', this.alpha[foot]);
        return topCover;
    }

    private async buildSandalPrintMeshes(mainMesh: THREE.Mesh, topCover: THREE.Mesh, foot: number): Promise<SandalPrintMeshes | null> {
        const [outSoleGeo, domeBigGeo, domeSmallGeo] = await Promise.all([
            LoadStl('/assets/bottom_outsole.stl'),
            LoadStl('/assets/main_band.stl'),
            LoadStl('/assets/top_band.stl'),
        ]);
        if (!outSoleGeo || !domeBigGeo || !domeSmallGeo) return null;

        mainMesh.geometry.computeBoundingBox();
        const mainBox = GetInsoleBox(mainMesh);
        if (!mainBox) return null;

        const mainSize = mainBox.getSize(new THREE.Vector3());
        const lengthScale = mainSize.z / INSOLE_LENGTH_MM;
        const mirror = foot === 1 ? -1 : 1;
        for (const geometry of [outSoleGeo, domeBigGeo, domeSmallGeo]) {
            geometry.scale(lengthScale * mirror, lengthScale, lengthScale);
        }

        outSoleGeo.computeBoundingBox();
        const outSoleCenter = outSoleGeo.boundingBox!.getCenter(new THREE.Vector3());
        const mainCenter = mainBox.getCenter(new THREE.Vector3());
        const align = mainCenter.sub(outSoleCenter);
        for (const geometry of [outSoleGeo, domeBigGeo, domeSmallGeo]) {
            geometry.translate(align.x, 0, align.z);
        }

        const position = this.insoles[foot].lowpolyBufferGeometry?.getAttribute('position');
        const bandPoint = new THREE.Vector3().fromBufferAttribute(position!, this.meshconfig.bandStartIndex);
        domeBigGeo.computeBoundingBox();
        const domeMin = bandPoint.y - domeBigGeo.boundingBox!.min.y;
        for (const geometry of [domeBigGeo, domeSmallGeo]) {
            geometry.translate(0, domeMin, 0);
        }

        topCover.geometry.computeBoundingBox();
        const topY = topCover.geometry.boundingBox?.max.y;
        if (topY == null) return null;

        const thicknessJoint = this.meshconfig.joints.surface.thickness;
        const thicknessY = position!.getY(thicknessJoint.centerId);
        const outsoleJoint = this.meshconfig.joints.surface.outsoleThickness;
        let thickness = thicknessY * thicknessJoint.bval + thicknessJoint.cval;
        if (outsoleJoint) {
            const outsoleY = position!.getY(outsoleJoint.centerId);
            thickness = (thicknessY - outsoleY - 2.55) * thicknessJoint.bval + thicknessJoint.cval;
        }
        const minY = thicknessY - thickness;
        const height = topY - minY;
        if (!(height > 0)) return null;

        const insoleGeo = new THREE.BoxGeometry(mainSize.x, height, mainSize.z);
        insoleGeo.translate(
            (mainBox.min.x + mainBox.max.x) / 2,
            minY + height / 2,
            (mainBox.min.z + mainBox.max.z) / 2,
        );

        return {
            insole: new THREE.Mesh(insoleGeo),
            main: new THREE.Mesh(),
            domeSmall: new THREE.Mesh(domeSmallGeo),
            domeBig: new THREE.Mesh(domeBigGeo),
            outSole: new THREE.Mesh(outSoleGeo),
        };
    }

    private exportStlBlob(mesh: THREE.Mesh, rotateX: boolean): Blob {
        const exporter = new STLExporter();
        if (rotateX) mesh.geometry.rotateX(Math.PI / 2);
        const data = exporter.parse(mesh, { binary: true });
        if (rotateX) mesh.geometry.rotateX(-Math.PI / 2);
        return new Blob([data], { type: 'application/octet-stream' });
    }

    async getGCodeFromServer(insoleIndex: number, topcover: THREE.Mesh, insole: THREE.Mesh, printerId: string, hardness: any, sandalPrint: SandalPrintMeshes | null = null): Promise<string | null | undefined> {
        if (!topcover) throw new Error("Top cover mesh is missing.");

        try {
            // Export Top Cover (already rotated into print space)
            const topCoverBlob = this.exportStlBlob(topcover, false);

            // Export Insole with the same X rotation used for the main mesh
            const insoleBlob = this.exportStlBlob(insole, true);

            const designId = useSelectedDesignStore.getState().designId;

            const formData = new FormData();
            formData.append('TopCover', topCoverBlob, 'TopCover.stl');
            formData.append('Insole', insoleBlob, 'Insole.stl');
            formData.append('PrinterId', printerId);
            formData.append('DesignId', designId.toString());
            formData.append('footSide', insoleIndex.toString());

            if (sandalPrint) {
                formData.append('Main', this.exportStlBlob(sandalPrint.main, true), 'Main.stl');
                formData.append('DomeSmall', this.exportStlBlob(sandalPrint.domeSmall, true), 'DomeSmall.stl');
                formData.append('DomeBig', this.exportStlBlob(sandalPrint.domeBig, true), 'DomeBig.stl');
                formData.append('OutSole', this.exportStlBlob(sandalPrint.outSole, true), 'OutSole.stl');
            }

            if (typeof hardness === 'object') {
                Object.keys(hardness).forEach(key => {
                    formData.append('Hardness', hardness[key]);
                });
            } else {
                formData.append('Hardness', hardness);
            }

            const response = await api.post(`/api/gcode/upload-stl`, formData, { timeout: 180000 });

            // Handle HTTP errors
            if (response.status === 400) throw new Error("Bad Request");
            if (response.status === 401) throw new Error("Unauthorized");
            if (response.status < 200 || response.status >= 300) throw new Error(`Server Error (${response.status})`);

            if (response.ok) {
                const zipArchive = await JSZip.loadAsync(response.body)
                // Try preferred file first
                let zipEntry = zipArchive.file("post_processed_result.gcode");

                // If not found, fall back to the first file in the archive
                if (!zipEntry) {
                    const zipEntries = Object.values(zipArchive.files);
                    if (zipEntries.length === 0) {
                        return "error: zip archive is empty!";
                    }
                    zipEntry = zipEntries[0]; // first file in archive
                }

                const gcodeContent = await zipEntry.async("string");
                return gcodeContent;
            }
        } catch (error: any) {
            console.error("getGCodeFromServer failed:", error);
            throw new Error(error?.message || "Unknown error occurred while generating GCode.");
        }
    }

    private generateColorConfig(gcode: string) {
        let colorConfig: any[] = [];
        const GCODE_COLORS = this.GCODE_COLORS;

        gcode.split("\n").forEach(function (line, i) {
            let color;
            if (line.startsWith(";TYPE:WALL-INNER")) {
                color = GCODE_COLORS.INNER;
            } else if (line.startsWith(";TYPE:WALL-OUTER")) {
                color = GCODE_COLORS.OUTER;
            } else if (line.startsWith(";TYPE:SKIRT")) {
                color = GCODE_COLORS.SKIRT;
            } else if (line.startsWith(";TYPE:FILL")) {
                color = GCODE_COLORS.FILL;
            } else if (line.startsWith(";TYPE:BOTTOM-FILL")) {
                color = GCODE_COLORS.BOTTOM_FILL;
            } else if (line.startsWith(";TYPE:INTERNAL-FILL")) {
                color = GCODE_COLORS.INTERNAL_FILL;
            } else if (line.startsWith(";TYPE:TOP-FILL")) {
                color = GCODE_COLORS.TOP_FILL;
            }

            if (colorConfig.length === 0 || color && colorConfig[colorConfig.length - 1].color !== color) {
                colorConfig.push({ toLine: i, color });
            } else {
                colorConfig[colorConfig.length - 1].toLine = i;
            }
        });

        return colorConfig;
    }

    async exportGCode(gcode: string, insole: THREE.Mesh, topCover: THREE.Mesh, insoleIndex: number): Promise<THREE.Mesh> {
        const slicedGCode = this.removeNozzleCleaning(gcode);
        if (this.isSandal) insole.geometry.rotateY(-Math.PI);

        insole.geometry.computeBoundingBox();
        const bbox = insole.geometry.boundingBox ?? new THREE.Box3();

        topCover.geometry.translate(-(bbox.min.x + bbox.max.x) / 2, - bbox.min.y, 0);
        topCover.geometry.rotateZ(Math.PI);
        topCover.geometry.applyMatrix4(this.transformationMatrix[insoleIndex].clone().invert());

        topCover.geometry.rotateX(-Math.PI / 2);
        topCover.geometry.computeBoundingBox();

        const renderer = new GCodeRenderer(800, 600, new Color(0x808080), topCover.clone());
        renderer.colorizer = new LineColorizer(this.generateColorConfig(slicedGCode));
        renderer.travelWidth = 0;
        renderer.skipLayers = this.isSandal ? 3 :
            bbox.getSize(new THREE.Vector3).z > 300 ? 2 : 1;
        renderer.gcode = slicedGCode;
        await renderer.render();

        this.insoles[insoleIndex].gcodeText = this.updateGCode(renderer.gcode, gcode);

        renderer.scene.traverse((child: any) => {
            if (child.isMesh) {
                if (child.geometry) {
                    child.geometry.rotateX(Math.PI / 2);
                    child.geometry.applyMatrix4(this.transformationMatrix[insoleIndex]);

                    if (!this.isSandal) child.geometry.rotateZ(Math.PI);

                    child.geometry.computeVertexNormals();
                    child.geometry.computeBoundingBox();

                    let gcodebb = new THREE.Box3().setFromObject(child);

                    if (this.isSandal) child.geometry.translate(bbox.max.x - gcodebb.max.x, -bbox.min.z, 0);
                    else child.geometry.translate(bbox.min.x - gcodebb.min.x, -bbox.max.z, 0);

                    child.geometry.rotateX(-Math.PI / 2);
                    child.geometry.computeVertexNormals();
                    child.geometry.normalizeNormals();
                }
            }
        });

        const sourceMesh = renderer.scene.children[0] as THREE.Mesh;
        const gcodeMesh = sourceMesh.clone();
        gcodeMesh.geometry = sourceMesh.geometry.clone();
        gcodeMesh.material = Array.isArray(sourceMesh.material)
          ? sourceMesh.material.map((m) => m.clone())
          : sourceMesh.material.clone();
        gcodeMesh.name = `gcode-${insoleIndex}`;

        renderer.dispose();
        return gcodeMesh;
    }

    removeNozzleCleaning(gcode: string) {
        const startIndex = gcode.indexOf(';LAYER_COUNT:');
        const endIndex = gcode.indexOf(';end');
        const gcodeSliced = gcode.slice(startIndex, endIndex);
        return gcodeSliced;
    }

    updateGCode(updated: string, original: string) {
        const startIndex = original.indexOf(';LAYER_COUNT:');
        const beginning = original.slice(0, startIndex);

        const endIndex = original.indexOf(';end');
        const end = original.slice(endIndex);

        return beginning + updated + end;
    }


}